import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns';

export type ReviewPricingInput = {
  /** What is charged today. The backend's prorated amount; it only differs from the monthly total
   *  when the purchase preview is available. */
  dueTodayCents: number;
  monthlyRateCents: number;
  monthlyTotalCents: number;
  now?: Date;
  serviceFeeCents: number;
  taxCents: number;
};

export type ReviewPricing = {
  dueToday: number;
  /** First day of next month, when the first full monthly payment is due. */
  nextDue: string;
  /** Parking coverage for the prorated period (due today minus the service fee and tax), or null
   *  when the amounts don't add up and the breakdown would mislead. */
  proratedCoverage: number | null;
  proratedPeriod: string;
};

const toDollars = (cents: number) => cents / 100;

/**
 * Shapes the review screen's money. The backend returns the prorated total and the monthly
 * total but no breakdown of the prorated charge, so the coverage line is derived assuming the
 * service fee and tax are flat (not prorated) — the same assumption the Figma numbers make.
 */
export function buildReviewPricing({
  dueTodayCents,
  now = new Date(),
  serviceFeeCents,
  taxCents,
}: ReviewPricingInput): ReviewPricing {
  const coverageCents = dueTodayCents - serviceFeeCents - taxCents;
  const lastDay = endOfMonth(now);
  const proratedPeriod =
    now.getDate() === lastDay.getDate()
      ? format(now, 'MMM d')
      : `${format(now, 'MMM d')}-${format(lastDay, 'd')}`;

  return {
    dueToday: toDollars(dueTodayCents),
    proratedCoverage: coverageCents > 0 ? toDollars(coverageCents) : null,
    nextDue: format(startOfMonth(addMonths(now, 1)), 'MMM d, yyyy'),
    proratedPeriod,
  };
}
